// © Microsoft Corporation. All rights reserved.

import { ProfileTabsTelemetryObjects, profileTabsTelemetryActionDispatchers, profileTabsTelemetryContracts } from "./ProfileTabs.telemetry";
import {activity, comment, subscriptions, user_following, user_locked, users2} from "../../utils/icons";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";

import { AccentButton } from "@microsoft/fast-components-react-msft";
import ProfileActivity from "../profile-activity/ProfileActivity";
import ProfileComments from "../profile-comments/ProfileComments";
import ProfilePrivacy from "../profile-privacy/ProfilePrivacy";
import ProfileSubscriptions from "../profile-subscriptions/ProfileSubscriptions";
import ProfileUsers from "../profile-users/ProfileUsers";
import React from "react";
import { SocialProps } from "../../Social.props";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { getContextData } from "@msnews/core";
import { hasThemeBase } from "../../utils/themeHelpers";
import { profileImages } from "../../utils/images";

export interface ProfileTabsProps extends SocialProps {
    emptyState?: string;
    activeTabs?: Array<string>;
}

interface ProfileTabsState {
    activeTab: Object;
}
export default class ProfileTabs extends React.Component<ProfileTabsProps, ProfileTabsState> {
    telemetryObjects: ProfileTabsTelemetryObjects;
    tabContentRef: React.RefObject<HTMLDivElement>;

    private static profileTabs = {
        Activity: {label: "Activity", icon: activity},
        Comments: {label: "Comments", icon: comment},
        Followers: {label: "Followers", icon: users2},
        Following: {label: "Following", icon: user_following},
        Privacy: {label: "Privacy", icon: user_locked},
        Subscriptions: {label: "Subscriptions", icon: subscriptions}
    };

    constructor(props) {
        super(props);

        this.state = {
            activeTab: null
        };
        this.telemetryObjects = null;
        this.tabContentRef = React.createRef();

        ProfileTabs.profileTabs = {
            Activity: {label: props.t.ProfileTabsActivityTab, icon: activity},
            Comments: {label: props.t.ProfileTabsCommentsTab, icon: comment},
            Followers: {label: props.t.ProfileTabsFollowersTab, icon: users2},
            Following: {label: props.t.ProfileTabsFollowingTab, icon: user_following},
            Privacy: {label: props.t.ProfileTabsPrivacyTab, icon: user_locked},
            Subscriptions: {label: props.t.ProfileTabsSubscriptionsTab, icon: subscriptions}
        };
    }

    componentDidMount() {
        if (this.props.activeTabs) {
            this.props.profileConnector && this.props.profileConnector.onProfilePageComponentConnected(this.props.id);
        }

        const requestContext = this.props.requestContext;
        const locationHref = getContextData(requestContext.data.locationHref, "locationHref");
        const tabNameFromUrl = Object.keys(ProfileTabs.profileTabs).filter(key => locationHref.toLowerCase().endsWith(key.toLowerCase())).pop();
        if (tabNameFromUrl) {
            this.setState({ activeTab: ProfileTabs.profileTabs[tabNameFromUrl] });
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.activeTabs && this.props.id != null && this.props.market != null && prevProps.id != this.props.id || prevProps.market != this.props.market) {
            this.props.profileConnector && this.props.profileConnector.onProfilePageComponentConnected(this.props.id);
        }

        /* when opened a new profile without rerendering component itself, reset the active tab */
        if (prevProps.id != this.props.id) {
            this.showTab(null, null, null);
        }
    }

    showTab(e, tab, telemetryObject) {
        this.setState({
            activeTab: tab
        });
        if (e) {
            document.getElementById("tabHeaders").scrollLeft = document.getElementById(tab.label).offsetLeft;
            telemetryObject && profileTabsTelemetryActionDispatchers.tabClicked(e.currentTarget, telemetryObject);
        }
        this.onTabChange(e);
    }

    onTabChange(e) {
        this.scrollToTop();
    }

    scrollToTop() {
        const shouldMove = this.tabContentRef && this.tabContentRef.current && this.tabContentRef.current.getBoundingClientRect().top < 55;
        if (shouldMove) { this.tabContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }
    }

    openOnboardingPopup() {
        this.props.popupConnector.openPopup("onboardingPage");
    }

    render() {
        if (this.props.id === "me" && !this.props.me) { return null; }
        const userId = this.props.id === "me" ? this.props.me.id : this.props.id;
        const {componentConnector, me, responsiveClass, t, users, managedClasses, usersConnector, profileConnector, commentConnector, market, popupConnector, emptyState, mainTelemetryObject, ugContentConnector, theme, enableSubscriptionFeatures, activeTabs } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        if (!users) { return null; }
        const user = users[userId] ? users[userId].data : null;
        if (!user) { return null; }
        const {comments, postsCount, isPrivateProfile, name, followers, following, isBanned, traceId} = user;
        const isSelf = !activeTabs && me && me.id == userId;
        const profileExistsButNotSocial = emptyState == enums.profilePageEmptyStates.NotSocialUser;
        let activeTab: any;
        if (profileExistsButNotSocial) {
            activeTab = null;
        } else if (isSelf && this.state.activeTab == null) {
            activeTab = ProfileTabs.profileTabs.Activity;
        } else if (!isSelf && (this.state.activeTab == ProfileTabs.profileTabs.Activity || this.state.activeTab == ProfileTabs.profileTabs.Privacy || this.state.activeTab == ProfileTabs.profileTabs.Subscriptions)) {
            activeTab = ProfileTabs.profileTabs.Comments;
        } else if (this.state.activeTab == ProfileTabs.profileTabs.Subscriptions && !enableSubscriptionFeatures) {
            activeTab = ProfileTabs.profileTabs.Activity;
        } else if (activeTabs && activeTabs.length > 0) {
            Object.keys(ProfileTabs.profileTabs).forEach(key => {
                // First element of activeTabs list accepted as activeTab
                if (ProfileTabs.profileTabs[key].label == activeTabs[0]) { activeTab = ProfileTabs.profileTabs[key]; }
            });
        } else {
            activeTab = this.state.activeTab || ProfileTabs.profileTabs.Comments;
        }
        const publishers = Object.keys(users).reduce((publishers, userId) => {
            if (users[userId].data && users[userId].data.subType === "Publisher") {
                return {...publishers, [userId]: users[userId].data};
            } else {
                return publishers;
            }
        }, {});

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            tabs: {},
            followers: addOrUpdateTelemetryObject(mainTelemetryObject, {...profileTabsTelemetryContracts.followers}, {}, this.telemetryObjects && this.telemetryObjects.followers),
            following: addOrUpdateTelemetryObject(mainTelemetryObject, {...profileTabsTelemetryContracts.following}, {}, this.telemetryObjects && this.telemetryObjects.following)
        };

        if ((isBanned && !isSelf) || emptyState == enums.profilePageEmptyStates.NoUser) {
            return (<div className={classNames(
                managedClasses.profileLockedContainer,
                [managedClasses.profileLockedSuperAppContainer, isSuperApp]
            )}>
                <img className={managedClasses.profileLockedIcon} alt={t.ImgAltPrivateProfile} src={profileImages.privateProfile}/>
                <div className="cwt-message">
                    {t.ProfileNotAvailableTxt}
                </div>
            </div>);
        } else if (isPrivateProfile && !isSelf) {
            return (<div className={classNames(
                managedClasses.profileLockedContainer,
                [managedClasses.profileLockedSuperAppContainer, isSuperApp]
            )}>
                <img className={managedClasses.profileLockedIcon} alt={t.ImgAltPrivateProfile} src={profileImages.privateProfile}/>
                <div className="cwt-message">
                    {`${name} ${t.ProfilePrivateAccountMsg}`}
                </div>
            </div>);
        } else {
            let followersTab: any;
            if (activeTab == ProfileTabs.profileTabs.Followers) {
                if (followers && followers.list && followers.list.length > 0) {
                    followersTab = <ProfileUsers {...{responsiveClass, componentConnector, t, user, managedClasses, users: followers, market, usersConnector, fetchUsers: profileConnector.fetchUserFollowers, mainTelemetryObject: this.telemetryObjects.followers}}/>;
                } else {
                    followersTab = <div className={classNames(
                        managedClasses.profileEmptyTabContainer,
                        [managedClasses.profileEmptyTabSuperAppContainer, isSuperApp]
                    )}>
                        <img src={profileImages.zeroUsers} alt={t.ImgAltNoFollowers}/>
                        <div className="cwt-message"> {isSelf ? `${t.NoFollowersTitleForUser}` : `${t.NoFollowersTitleForOthers}`} </div>
                        <div className="cwt-sub-message"> {isSelf ? `${t.NoFollowersTextForUser}` : `${t.NoFollowersTextForOthers}`} </div>
                    </div>;
                }
            } else {
                followersTab = "";
            }

            let followingTab: any;
            if (activeTab == ProfileTabs.profileTabs.Following) {
                if (following && following.list && following.list.length > 0) {
                    followingTab = <ProfileUsers {...{responsiveClass, componentConnector, t, user, managedClasses, users: following, market, usersConnector, fetchUsers: profileConnector.fetchUserFollowings, mainTelemetryObject: this.telemetryObjects.following}}/>;
                } else {
                    followingTab = <div className={managedClasses.profileEmptyTabContainer}>
                        <img src={profileImages.zeroUsers} alt={t.ImgAltNoFollowings}/>
                        <div className="cwt-message"> {isSelf ? `${t.NoFollowingTitleForUser}` : `${t.NoFollowingTitleForOthers}`} </div>
                        <div className="cwt-sub-message"> {isSelf ? `${t.NoFollowingTextForUser}` : ""} </div>
                    </div>;
                }
            } else {
                followingTab = "";
            }

            return (<div role="main" className={classNames(
                "cwt-profile-tabs-container",
                managedClasses.profileTabsContainer,
                [managedClasses.profileTabsSuperAppContainer, isSuperApp],
                responsiveClass
            )}>
                <ul id="tabHeaders" className={`cwt-tabs cwt-profile-tabs no-select ${profileExistsButNotSocial ? "disabled" : ""} ${getContextData(this.props.requestContext.data.locationHostName, "locationHostName")  == "localhost.msn.com" ||
                getContextData(this.props.requestContext.data.locationHostName, "locationHostName")  == "assets.msn.com"? "demo": ""}`} role="tablist">
                    { Object.keys(ProfileTabs.profileTabs).map(key => {
                        const tab = ProfileTabs.profileTabs[key];
                        if (tab == ProfileTabs.profileTabs.Activity && !isSelf && !profileExistsButNotSocial) { return ""; }
                        if (tab == ProfileTabs.profileTabs.Privacy && !isSelf && !profileExistsButNotSocial) { return ""; }
                        if (tab == ProfileTabs.profileTabs.Subscriptions && (!isSelf || !enableSubscriptionFeatures)) { return ""; }
                        if (activeTabs && activeTab && activeTab != tab) { return ""; }

                        this.telemetryObjects.tabs[key] = addOrUpdateTelemetryObject(mainTelemetryObject, profileTabsTelemetryContracts.tabs[key], {id: traceId}, this.telemetryObjects && this.telemetryObjects.tabs[key]);
                        return (
                            <li role="tab" id={tab.label} key={tab.label} className={`${activeTab == tab && "active"} cwt-tab-item cwt-link`} onClick={(e) => this.showTab(e, tab, this.telemetryObjects.tabs[key])}
                                aria-selected={activeTab == tab}
                                {...renderHybrid(this.telemetryObjects.tabs[key], false)}
                            >
                                <i className="cwt-icon">
                                    {tab.icon}
                                    <span className="cwt-icon-label">{tab.label}</span>
                                </i>
                            </li>
                        );
                    }
                    )}
                </ul>

                <div ref={this.tabContentRef} className="cwt-tab-content">
                    { activeTab == ProfileTabs.profileTabs.Activity &&
                        <ProfileActivity {...{responsiveClass, componentConnector, user, t, managedClasses, users, usersConnector, market, popupConnector, commentConnector, mainTelemetryObject }}/>
                    }

                    { activeTab == ProfileTabs.profileTabs.Comments &&
                        <ProfileComments {...{componentConnector, isSelf, me, user, comments, postsCount, t, managedClasses, users, profileConnector, commentConnector, usersConnector, market, popupConnector, mainTelemetryObject, responsiveClass }}/>
                    }

                    { followersTab }

                    { followingTab }

                    { activeTab == ProfileTabs.profileTabs.Subscriptions &&
                      <ProfileSubscriptions { ...{...this.props, user, publishers, mainTelemetryObject }}/>
                    }

                    { activeTab == ProfileTabs.profileTabs.Privacy &&
                        <ProfilePrivacy { ...{...this.props, user, mainTelemetryObject }}/>
                    }

                    { !activeTab && profileExistsButNotSocial
                        ? <div className={classNames(
                            managedClasses.profileEmptyTabContainer,
                            [managedClasses.profileEmptyTabSuperAppContainer, isSuperApp]
                        )}>
                            <img src={profileImages.zeroActivity} alt={t.ImgAltPrivateProfile}/>
                            <div className="cwt-message"> {t.ProfileNotSocialUserJoinMessage} </div>
                            <AccentButton className="cwt-fast-button cwt-popup-button" onClick={this.openOnboardingPopup.bind(this)}> {t.ProfileNotSocialUserJoinButton}</AccentButton>
                        </div>
                        : ""
                    }
                </div>
            </div>);
        }
    }
}
